import { Lexer, type Token } from 'marked';

export interface DocumentSection {
  id: string;
  level: number;
  title: string;
  content: string;
  tokens: Token[];
  subsections: DocumentSection[];
}

export interface ParsedDocument {
  title?: string;
  sections: DocumentSection[];
  flatSections: DocumentSection[];
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 60);
}

function makeId(title: string, existing: Set<string>): string {
  let id = slugify(title) || 'section';
  if (existing.has(id)) {
    let i = 2;
    while (existing.has(`${id}-${i}`)) i++;
    id = `${id}-${i}`;
  }
  existing.add(id);
  return id;
}

export function parseMarkdownToSections(markdown: string): ParsedDocument {
  const lexer = new Lexer();
  const tokens = lexer.lex(markdown);

  const flatSections: DocumentSection[] = [];
  const sections: DocumentSection[] = [];
  const idSet = new Set<string>();
  const stack: DocumentSection[] = [];

  let currentBody: Token[] = [];
  let firstH1: string | undefined;

  const flushSection = (level: number, title: string) => {
    while (stack.length > 0 && stack[stack.length - 1].level >= level) {
      const closing = stack.pop();
      if (closing) {
        closing.tokens.push(...currentBody);
        currentBody = [];
      }
    }

    const parent = stack[stack.length - 1];
    const section: DocumentSection = {
      id: makeId(title, idSet),
      level,
      title,
      content: '',
      tokens: currentBody,
      subsections: [],
    };
    currentBody = [];
    flatSections.push(section);

    if (parent) {
      parent.subsections.push(section);
    } else {
      sections.push(section);
    }

    stack.push(section);
    return section;
  };

  for (const token of tokens) {
    if (token.type === 'heading') {
      const heading = token as HeadingToken;
      if (heading.depth === 1 && !firstH1) {
        firstH1 = extractText(token);
        continue;
      }
      if (heading.depth >= 2) {
        const title = extractText(token);
        flushSection(heading.depth, title);
        continue;
      }
    }
    currentBody.push(token);
  }

  if (currentBody.length > 0) {
    const last = stack[stack.length - 1];
    if (last) {
      last.tokens.push(...currentBody);
    } else {
      const orphanSection: DocumentSection = {
        id: makeId('content', idSet),
        level: 2,
        title: 'Content',
        content: '',
        tokens: currentBody,
        subsections: [],
      };
      sections.push(orphanSection);
      flatSections.push(orphanSection);
    }
    currentBody = [];
  }

  for (const section of flatSections) {
    section.content = tokensToMarkdown(section.tokens);
  }

  return {
    title: firstH1,
    sections,
    flatSections,
  };
}

function extractText(token: Token): string {
  if ('text' in token && typeof token.text === 'string') return token.text;
  if ('tokens' in token && Array.isArray(token.tokens)) {
    return token.tokens.map(extractText).join('');
  }
  return '';
}

function tokensToMarkdown(tokens: Token[]): string {
  const parts: string[] = [];
  for (const token of tokens) {
    const md = tokenToMarkdown(token);
    if (md.trim() === '') continue;
    parts.push(md);
  }
  return parts.join('\n\n').trim();
}

type HeadingToken = Token & {
  type: 'heading';
  depth: number;
};

type ListToken = Token & {
  type: 'list';
  ordered: boolean;
  items: (Token & { type: 'list_item'; tokens: Token[] })[];
};

type ListItemToken = Token & {
  type: 'list_item';
  tokens: Token[];
};

type TableToken = Token & {
  type: 'table';
  header: { tokens: Token[] }[];
  rows: { tokens: Token[] }[][];
};

type CodeToken = Token & {
  type: 'code';
  text: string;
  lang?: string;
};

type HTMLToken = Token & {
  type: 'html';
  text: string;
  block?: boolean;
};

function tokenToMarkdown(token: Token, depth = 0): string {
  switch (token.type) {
    case 'paragraph': {
      const t = token as Token & { tokens?: Token[] };
      return inlineToMarkdown(t.tokens || []);
    }
    case 'text': {
      const t = token as Token & { text?: string; tokens?: Token[] };
      if (t.tokens) return inlineToMarkdown(t.tokens);
      return t.text || '';
    }
    case 'heading': {
      const t = token as HeadingToken;
      return `${'#'.repeat(t.depth)} ${inlineToMarkdown((token as Token & { tokens?: Token[] }).tokens || [])}`;
    }
    case 'list': {
      const list = token as ListToken;
      return list.items.map((item, i) => renderListItem(item, list.ordered, i, depth)).join('\n');
    }
    case 'blockquote': {
      const t = token as Token & { tokens?: Token[] };
      const inner = (t.tokens || [])
        .map((child) => tokenToMarkdown(child, depth))
        .join('\n\n')
        .trim();
      if (!inner) return '';
      return inner
        .split('\n')
        .map((line) => `> ${line}`)
        .join('\n');
    }
    case 'code': {
      const code = token as CodeToken;
      return code.lang
        ? `\`\`\`${code.lang}\n${code.text}\n\`\`\``
        : `\`\`\`\n${code.text}\n\`\`\``;
    }
    case 'hr':
      return '---';
    case 'table': {
      const table = token as TableToken;
      const header = `| ${table.header.map((h) => inlineToMarkdown(h.tokens)).join(' | ')} |`;
      const align = `| ${table.header.map(() => '---').join(' | ')} |`;
      const rows = table.rows
        .map((row) => `| ${row.map((cell) => inlineToMarkdown(cell.tokens)).join(' | ')} |`)
        .join('\n');
      return `${header}\n${align}\n${rows}`;
    }
    case 'space':
      return '';
    case 'html': {
      const html = token as HTMLToken;
      return html.text || '';
    }
    default: {
      const t = token as Token & { text?: string };
      return t.text || '';
    }
  }
}

function renderListItem(item: ListItemToken, ordered: boolean, index: number, depth: number): string {
  const marker = ordered ? `${index + 1}.` : '-';
  const indent = '  '.repeat(depth);
  const textParts: string[] = [];
  const subLists: string[] = [];

  for (const child of item.tokens || []) {
    if (child.type === 'list') {
      subLists.push(tokenToMarkdown(child, depth + 1));
    } else {
      const rendered = tokenToMarkdown(child, depth);
      if (rendered) textParts.push(rendered);
    }
  }

  const text = textParts.join('\n');
  const sub = subLists.join('\n');
  if (!text && !sub) return '';

  const result = [`${indent}${marker} ${text}`];
  if (sub) result.push(sub);
  return result.join('\n');
}

type InlineToken = Token & {
  type: string;
  text?: string;
  tokens?: Token[];
  href?: string;
  title?: string;
};

function inlineToMarkdown(tokens: Token[]): string {
  return tokens
    .map((token) => {
      const t = token as InlineToken;
      switch (t.type) {
        case 'text':
        case 'html':
          return t.text || '';
        case 'strong':
          return `**${inlineToMarkdown(t.tokens || [])}**`;
        case 'em':
          return `*${inlineToMarkdown(t.tokens || [])}*`;
        case 'codespan':
          return `\`${t.text}\``;
        case 'del':
          return `~~${inlineToMarkdown(t.tokens || [])}~~`;
        case 'link':
          return `[${inlineToMarkdown(t.tokens || [])}](${t.href}${t.title ? ` "${t.title}"` : ''})`;
        case 'image':
          return `![${t.text}](${t.href}${t.title ? ` "${t.title}"` : ''})`;
        case 'br':
          return '\n';
        default: {
          if (t.tokens) return inlineToMarkdown(t.tokens);
          return t.text || '';
        }
      }
    })
    .join('');
}
